'use client';

/**
 * AssignmentTypeCard — card de producto "Apartamento por Asignación".
 * = AvailabilityApartmentCard con los deltas de la spec §4.0 del plan: mismo
 * frame/overlay/precio/meta, badge sobrio "Asignación garantizada", 3 chips
 * de garantía bajo el precio, disclosure de fotos y CTA que abre el modal de
 * orden (no hay ficha de tipo). Cero tokens nuevos.
 *
 * Copy F0.5 + F1.5 (WEB-F1 W6 / PLAN-ACTIVACION §3.5): sin "pendiente por
 * asignar" ni afines — se vende como categoría de hotel. El número de unidad
 * es reactivo (solo si preguntan, se confirma antes del check-in), NUNCA una
 * advertencia previa. F1.5 suma la escasez honesta + modelo mental de hotel
 * en assignment-results.tsx (kicker de la sección) y las 3 chips aquí.
 */
import { useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { ArrowRight, Building2, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { companyConfig } from '@/lib/config-loader';
import { getBlurDataUrl, versionedImageUrl } from '@/lib/image-version';
import { trackPostHogEvent, type CTAEventProps } from '@/lib/analytics/posthog';
import { openTrackedWhatsApp } from '@/lib/whatsapp/open-tracked-whatsapp';
import ApartmentCardMediaOverlay from '@/components/apartments/card/ApartmentCardMediaOverlay';
import { formatMoney } from '@/components/apartments/card/apartment-card-utils';
import type { AssignmentTypeCardDTO } from '@/lib/assignment/quote';

interface AssignmentTypeCardProps {
  product: AssignmentTypeCardDTO;
  arrival: string;
  departure: string;
  people: number;
  ctaAttribution: CTAEventProps;
  onReserve: (product: AssignmentTypeCardDTO) => void;
}

function formatDate(value: string): string {
  if (!value) return '';
  return new Date(`${value}T00:00:00`).toLocaleDateString('es-CO', {
    day: 'numeric',
    month: 'short',
  });
}

export default function AssignmentTypeCard({
  product,
  arrival,
  departure,
  people,
  ctaAttribution,
  onReserve,
}: AssignmentTypeCardProps) {
  const [imageIndex, setImageIndex] = useState(0);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const images = product.photos;
  const imageCount = images.length;
  const currentImage = images[imageIndex];

  const whatsappMessage = useMemo(() => {
    return [
      `Hola, me interesa "${product.displayName}" (precio fijo por temporada) en el ${product.buildingName}.`,
      arrival && departure ? `Fechas: ${formatDate(arrival)} al ${formatDate(departure)}.` : '',
      people ? `Somos ${people} ${people === 1 ? 'persona' : 'personas'}.` : '',
      `Total cotizado: ${formatMoney(product.totalPerUnit)} COP por ${product.nights} ${product.nights === 1 ? 'noche' : 'noches'}.`,
    ]
      .filter(Boolean)
      .join(' ');
  }, [product, arrival, departure, people]);

  const whatsappHref = useMemo(
    () =>
      `https://wa.me/${companyConfig.contact.whatsapp.number}?text=${encodeURIComponent(whatsappMessage)}`,
    [whatsappMessage]
  );

  const goToImage = (direction: -1 | 1) => {
    if (imageCount <= 1) return;
    setImageIndex((current) => (current + direction + imageCount) % imageCount);
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const t = event.changedTouches[0];
    const dx = touchStart.current.x - (t?.clientX ?? 0);
    const dy = touchStart.current.y - (t?.clientY ?? 0);
    touchStart.current = null;
    if (Math.abs(dx) < 34) return;
    if (Math.abs(dx) <= Math.abs(dy)) return;
    event.preventDefault();
    goToImage(dx > 0 ? 1 : -1);
  };

  const handleReserve = () => {
    trackPostHogEvent('assignment_card_cta_clicked', {
      ...ctaAttribution,
      product_slug: product.slug,
      product_name: product.displayName,
      total_cop: product.totalPerUnit,
      deposit_cop: product.depositCop,
      nights: product.nights,
      num_adults: people,
      cta_position: 'assignment_card',
      cta_label: 'Reservar',
    });
    onReserve(product);
  };

  const ariaLabel = `Reservar ${product.displayName}, hasta ${product.capacityPerUnit} personas, ${formatMoney(product.totalPerUnit)} total por ${product.nights} noches, asignación garantizada en el ${product.buildingName}`;

  return (
    <article
      className="group relative w-full overflow-hidden rounded-2xl border border-[#E8E1D4] bg-[#FDFBF7] shadow-[0_2px_16px_-4px_rgba(0,0,0,0.06)] transition-all duration-300 hover:-translate-y-px hover:border-[#D8D1C5] hover:shadow-[0_14px_34px_-26px_rgba(15,23,42,0.45)]"
      data-assignment-product={product.slug}
      onTouchStart={(event) => {
        const t = event.changedTouches[0];
        touchStart.current = t ? { x: t.clientX, y: t.clientY } : null;
      }}
      onTouchEnd={handleTouchEnd}
    >
      {/* Frame estirado: toda la card abre el modal (no hay ficha de tipo) */}
      <button
        type="button"
        onClick={handleReserve}
        aria-label={ariaLabel}
        className="absolute inset-0 z-10 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
      >
        <span className="sr-only">{ariaLabel}</span>
      </button>

      {/* === ZONA VISUAL (carrusel de la unidad representativa) === */}
      <div className="relative aspect-[3/2] w-full overflow-hidden bg-stone-100 sm:aspect-[4/3]">
        {currentImage ? (
          <Image
            src={versionedImageUrl(currentImage)}
            alt={`${product.displayName} en el ${product.buildingName}, ${product.neighborhood}`}
            fill
            sizes="(max-width: 640px) 95vw, (max-width: 1024px) 45vw, 380px"
            className="pointer-events-none object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
            quality={78}
            placeholder="blur"
            blurDataURL={getBlurDataUrl(versionedImageUrl(currentImage))}
          />
        ) : (
          <div className="pointer-events-none flex h-full w-full items-center justify-center bg-stone-100 text-stone-500">
            <Building2 className="h-10 w-10" strokeWidth={1.5} />
          </div>
        )}

        {/* Sin rating ni logos (no hay misatribución de reseñas en productos).
            Badge SOBRIO (spec F1.5 jerarquía visual): mismo pill blanco que
            usan las demás cards, texto neutro — nada "distinto/raro" que
            despierte sospecha. "Asignación garantizada" es más largo que el
            "Precio fijo"/"Mínimo" de las demás cards — titleClassName amplía
            el padding-derecho del título (twMerge pisa el pr-20 default) para
            que no se solape con el pill en cards angostas. */}
        <ApartmentCardMediaOverlay
          title={product.displayName}
          neighborhood={product.neighborhood}
          titleClassName="pr-40"
          topRight={
            <span className="rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-slate-700 shadow-sm">
              Asignación garantizada
            </span>
          }
        />

        {imageCount > 1 ? (
          <>
            <button
              type="button"
              aria-label="Foto anterior"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                goToImage(-1);
              }}
              className="absolute left-1 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center bg-transparent text-white drop-shadow-[0_2px_7px_rgba(0,0,0,0.75)] transition hover:text-white focus-visible:rounded-full focus-visible:bg-black/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
            >
              <ChevronLeft className="h-6 w-6" strokeWidth={2.35} />
            </button>
            <button
              type="button"
              aria-label="Siguiente foto"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                goToImage(1);
              }}
              className="absolute right-1 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center bg-transparent text-white drop-shadow-[0_2px_7px_rgba(0,0,0,0.75)] transition hover:text-white focus-visible:rounded-full focus-visible:bg-black/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
            >
              <ChevronRight className="h-6 w-6" strokeWidth={2.35} />
            </button>
            <span className="pointer-events-none absolute bottom-3 right-3 z-20 hidden rounded-full bg-black/45 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm transition-opacity sm:block sm:opacity-0 sm:group-hover:opacity-100">
              {imageIndex + 1} / {imageCount}
            </span>
          </>
        ) : null}
      </div>

      {/* === ZONA INFO === */}
      <div className="p-4">
        <div className="mb-2.5 space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-medium text-gray-600">
              Total por {product.nights} {product.nights === 1 ? 'noche' : 'noches'}:
            </span>
            <span className="text-lg font-semibold text-gray-900 tabular-nums">
              {formatMoney(product.totalPerUnit)}
            </span>
          </div>
          <div className="text-xs text-gray-600">
            Incluye aseo y registro · Reserva con solo{' '}
            <span className="font-semibold text-gray-950 tabular-nums">
              {formatMoney(product.depositCop)}
            </span>
          </div>

          {/* 3 chips de garantía (spec F1.5 principio 3): certeza visible
              bajo el precio, NO letra pequeña. */}
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            <GuaranteeChip text="Misma categoría garantizada (capacidad, vista al mar, dotación)" />
            <GuaranteeChip text={product.buildingName} />
            <GuaranteeChip text="Precio fijo por temporada" />
          </div>

          <p className="text-xs text-gray-500">
            Las fotos muestran una unidad representativa de esta categoría. El piso y la
            decoración pueden variar; lo que dice el nombre del producto sí está garantizado.
          </p>
          {/* Línea de confianza (F0.5/principio 4): más peso visual que el
              disclosure de arriba — es la promesa que más tranquiliza. */}
          <p className="text-xs font-medium text-gray-700">
            Tu reserva queda confirmada al instante; el número exacto de tu unidad te lo
            confirmamos antes del check-in.
          </p>
        </div>

        {/* Meta line: capacidad/tipo (el edificio ya quedó dicho en el chip) */}
        <p className="mt-2 border-t border-[#E8E1D4] pt-2 text-center text-[13px] leading-relaxed text-gray-600">
          {product.baseTypeLabel} · Hasta {product.capacityPerUnit} personas
        </p>

        <button
          type="button"
          onClick={handleReserve}
          className="relative z-20 mt-3 flex w-full min-h-[44px] items-center justify-center rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-px hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          Reservar
        </button>
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => {
            event.preventDefault();
            openTrackedWhatsApp(whatsappMessage, {
              ...ctaAttribution,
              apartment_name: product.displayName,
              num_adults: people,
              cta_position: 'assignment_card',
              cta_label: 'Preguntar por WhatsApp',
              has_pricing: true,
            });
          }}
          className="relative z-20 mt-2 inline-flex min-h-[44px] items-center gap-1.5 py-2 text-sm font-semibold text-gray-900 transition-colors hover:text-emerald-800"
        >
          <span>Preguntar por WhatsApp</span>
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
        </a>
      </div>
    </article>
  );
}

/** Chip sobrio de garantía (spec F1.5 principio 3) — check + texto corto, sin ruido. */
function GuaranteeChip({ text }: { text: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-1 text-[11px] font-medium leading-tight text-stone-700">
      <CheckCircle2 className="h-3 w-3 flex-shrink-0 text-emerald-600" strokeWidth={2.5} />
      {text}
    </span>
  );
}
